<style>
  div.blurry {
    color: transparent;
    text-shadow: 0 0 8px #000;
  }
</style>

<script lang="ts" context="module">
  // keep track of what market id is being shown so that only one market is shown at a time
  const marketIdShown = writable('');
</script>

<script lang="ts">
  import { browser } from '$app/environment';
  import type { BookmakerMarket, MarketType, Runner } from '$types/races';
  import { isSubscribed } from '$utils/auth';
  import { currentTime } from '$utils/time';
  import { activeSiteConfig, lastBetslipPrefillAlert } from '$utils/ui';
  import { createFloatingActions } from 'svelte-floating-ui';
  import { flip, offset, shift } from 'svelte-floating-ui/dom';
  import { writable, type Readable } from 'svelte/store';
  import RunnerHoverPopup from './RunnerHoverPopup.svelte';
  import { page } from '$app/stores';
  import { prefillBookmakers } from '$utils/bookmakers';
  import { isRaceClosed } from '$utils/race';
  import toast from 'svelte-french-toast';
  import ClickableToast from '$components/toasts/ClickableToast.svelte';
  import { openPreferencesTab } from '$components/preferences/PreferencesTabs.svelte';
  import { goto } from '$app/navigation';

  const [floatingRef, floatingContent] = createFloatingActions({
    strategy: 'absolute',
    placement: 'right',

    middleware: [offset(-3), flip(), shift()]
  });

  $: ({ streamed, user, race, hasCompanion, featureFlags } = $page.data);
  $: raceLinks = streamed?.raceData?.data?.race.links;

  export let runner: Runner;
  export let market: Readable<BookmakerMarket> | undefined;
  export let marketType: MarketType;
  export let topPrices: number[];

  const randomNumber = () => {
    // generate a random float between 2 and 10
    // rounded to 1 decimal place
    return Math.round((Math.random() * 8 + 2) * 10) / 10;
  };

  $: topPriceIndex = 0;
  $: {
    if ($market?.[marketType].price)
      topPriceIndex = topPrices.indexOf($market?.[marketType].price);
  }

  let showDialog = false;

  //   calculate the price to show
  $: showPrice = 0;
  $: {
    // alter the show price if not subscribed
    if (!$isSubscribed) {
      // check if it is a top price
      if (topPriceIndex !== 0) {
        showPrice = 0;
      } else {
        showPrice = $market?.[marketType].price ?? 0;
      }
    } else {
      showPrice = $market?.[marketType].price ?? 0;
      if (!showPrice && $race && isRaceClosed($race)) {
        // if race is closed and no price is available, show the last nonzero fluc
        showPrice =
          $market?.[marketType].flucs
            ?.filter((fluc) => fluc.price !== 0)
            .slice(-1)[0]?.price ?? 0;
      }
    }
  }
  // round the price to 2 decimal places
  $: roundedPrice = showPrice.toFixed(2);

  // determine which direction the price is moving
  let flucDirection: 'UP' | 'DOWN' | 'NONE' = 'NONE';
  $: {
    if ($market) {
      if (!$market[marketType]?.previousPrice) flucDirection = 'NONE';
      else if (
        $market[marketType].price > ($market[marketType]?.previousPrice ?? 0)
      )
        flucDirection = 'UP';
      else if (
        $market[marketType].price < ($market[marketType]?.previousPrice ?? 0)
      )
        flucDirection = 'DOWN';
      else flucDirection = 'NONE';
    }
  }

  // determine if the market has been updated recently
  let backgroundClass = '';
  $: {
    if ($market) {
      if (
        browser &&
        $currentTime.valueOf() - $market[marketType].lastUpdated.valueOf() <
          2500
      ) {
        if (flucDirection === 'UP')
          backgroundClass = $activeSiteConfig.alternateColourMode
            ? 'from-yellow-400 to-yellow-200'
            : 'from-green-200 to-green-100';
        else if (flucDirection === 'DOWN')
          backgroundClass = $activeSiteConfig.alternateColourMode
            ? 'from-red-300 to-red-500'
            : 'from-red-100 to-red-200';
        else backgroundClass = '';
      } else {
        backgroundClass = '';
      }

      // if price is none we stop here
      if ($market?.[marketType].price) {
        // check top price
        if (topPriceIndex === 0) {
          backgroundClass +=
            ' from-sky-200 to-sky-100 shadow-inner hover:from-sky-200 hover:to-sky-200';
        } else if (topPriceIndex === 1 && $isSubscribed) {
          backgroundClass +=
            ' from-sky-100 to-white shadow-inner hover:from-sky-100 hover:to-sky-100';
        } else if (topPriceIndex === 2 && $isSubscribed) {
          // do nothing for now
          // backgroundClass += ' from-sky-100 to-sky-200 shadow-inner hover:bg-slate-300';
        }
      }
    }
  }

  let showDialogTimeout: ReturnType<typeof setTimeout>;
  let loadingTimeout: ReturnType<typeof setTimeout>;

  let loading = false;
  let fetchMarketData: () => Promise<boolean>;
  async function handleMouseEnter() {
    marketIdShown.set($market?.id ?? '');
    showDialogTimeout = setTimeout(
      async () => {
        if (!$market?.id) return;
        loading = true;
        loadingTimeout = setTimeout(() => {
          showDialog = true;
        }, 200);
        await fetchMarketData?.();
        loading = false;
        showDialog = true;
      },
      $isSubscribed ? 750 : 1000
    );
  }

  $: $marketIdShown !== $market?.id && handleMouseLeave(); // hide if another market is shown
  async function handleMouseLeave() {
    clearTimeout(showDialogTimeout);
    clearTimeout(loadingTimeout);
    showDialog = false;
    loading = false;
  }

  async function handlePlaceBet() {
    // if (!$featureFlags?.includes('bet_prefill')) return;

    // if mobile, return
    if (window.innerWidth < 768) return;

    const prefillConfig = user?.data?.user.siteConfig.prefill;

    if (!$isSubscribed) {
      // show toast to subscribe
      toast(ClickableToast, {
        icon: '🔒',
        duration: 5000,
        position: 'bottom-center',
        // @ts-ignore - svelte-french-toast doesn't support custom props
        props: {
          content: 'Subscribe to enable betslip fill',
          subtitle: 'Automatically fill your betslip with your ideal stake',
          onClick: async () => {
            await goto('/subscribe/racing');
          }
        }
      });
      return;
    }

    if (!$hasCompanion) {
      // check if it has been long enough
      if ($currentTime.valueOf() - $lastBetslipPrefillAlert.valueOf() < 10000) {
        return;
      } else {
        lastBetslipPrefillAlert.set($currentTime.valueOf());
      }

      toast(ClickableToast, {
        icon: '🤖',
        duration: 5000,
        position: 'bottom-center',
        // @ts-ignore - svelte-french-toast doesn't support custom props
        props: {
          content: 'Click to Enable Betslip Prefill',
          subtitle: 'Automatically fill your betslip with your ideal stake',
          onClick: () => {
            openPreferencesTab('Betting');
          }
        }
      });
      return;
    }

    if (!prefillConfig?.quickFill) {
      // check if it has been long enough
      if (
        $currentTime.valueOf() - $lastBetslipPrefillAlert.valueOf() <
        1000000
      ) {
        return;
      } else {
        lastBetslipPrefillAlert.set($currentTime.valueOf());
      }

      toast(ClickableToast, {
        icon: '💡',
        duration: 5000,
        position: 'bottom-center',
        // @ts-ignore - svelte-french-toast doesn't support custom props
        props: {
          content: 'Quick Fill is disabled',
          subtitle: 'Click to open Preferences and enable it',
          onClick: () => {
            openPreferencesTab('Betting');
          }
        }
      });
      return;
    }

    if ($market?.bookmaker && !prefillBookmakers.includes($market.bookmaker)) {
      toast(ClickableToast, {
        icon: '🙁',
        duration: 5000,
        position: 'bottom-center',
        // @ts-ignore - svelte-french-toast doesn't support custom props
        props: {
          content: 'Quick Fill is not available for this bookmaker',
          onClick: () => {}
        }
      });
      return;
    }

    // craft a link to the bookmaker's website
    // find the bookmaker navlink
    const navLink = raceLinks?.find((link) => {
      return link.bookmaker === $market?.bookmaker;
    });
    if (!navLink?.navLink || !$market) return;
    const url = new URL(navLink.navLink);
    // add query params (bwSelection)
    url.searchParams.append('bwSelection', runner.number.toString());
    let raceTypeConfig = prefillConfig?.defaults?.thoroughbred;
    if ($race?.meeting.type === 'Harness') {
      raceTypeConfig = prefillConfig?.defaults?.harness;
    } else if ($race?.meeting.type === 'Greyhound') {
      raceTypeConfig = prefillConfig?.defaults?.greyhound;
    }
    let stake = raceTypeConfig?.value ?? 0;
    if (raceTypeConfig?.type === 'TO_WIN') {
      const toWin = raceTypeConfig?.value ?? 0;
      stake = toWin / ($market[marketType].price - 1);
    }

    // round down to nearest whole number
    stake = Math.floor(stake);
    if (stake === 0 && raceTypeConfig.value) {
      stake = 1;
    }

    url.searchParams.append('bwStake', stake.toString());

    if (marketType === 'fixedPlace') {
      url.searchParams.append('bwBetType', 'plc');
    }

    // open the link in a new tab
    window.open(url.toString(), '_blank');
  }
</script>

<td
  on:click={handlePlaceBet}
  on:keypress={handlePlaceBet}
  on:mouseenter={handleMouseEnter}
  on:mouseleave={handleMouseLeave}
  use:floatingRef
  class="table-cell cursor-pointer select-none whitespace-nowrap border bg-gradient-to-br text-[13px] transition-all duration-200 ease-in-out hover:bg-slate-100 hover:shadow-inner
     {backgroundClass}
    "
>
  {#if $isSubscribed || showPrice !== 0}
    <div
      class="flex h-8 w-8 flex-col items-center justify-center {showPrice ===
        0 && 'text-gray-400'}"
    >
      {showPrice !== 0 ? showPrice : '-'}
    </div>
  {:else}
    <div class="blurry flex h-8 w-8 flex-col items-center justify-center">
      {randomNumber() ?? '-'}
    </div>
  {/if}

  <RunnerHoverPopup
    {showDialog}
    {runner}
    {market}
    {marketType}
    {floatingContent}
    bind:loading
    bind:fetchMarketData
  />
</td>
